import { FormsElementTypeEnum, WorkflowContainerFragment } from 'generated/resources/graphql'

import { Form } from 'modules/forms'
import { Element } from 'modules/forms/lib/Element'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import {
  buildKey as buildTriggerFormKey,
  parseKey as parseTriggerFormKey,
} from 'modules/procurement/lib/dynamicInputCases/triggerFormCase'
import { NodeUI, NodeUICondition } from 'modules/workflows/components/Node/types'
import { convertArrayFromWorkflowValue } from 'modules/workflows/components/Sidepanel/utils'
import { deserializeWorkflowNodes } from 'modules/workflows/lib/backend-adapter'
import { isBackendSwitchNode } from 'modules/workflows/lib/backend-types'

type WorkflowCategory = 'string' | 'number' | 'singleSelect' | 'multiSelect'

type FormElementInfo = {
  workflowCategory: WorkflowCategory
  optionLabels: Set<string>
}

/**
 * Maps a frontend element type to the workflow category that the backend
 * generates for `DynamicInputDataFromType`.
 */
function getWorkflowCategory(elementType: FormsElementTypeEnum): WorkflowCategory | null {
  switch (elementType) {
    case 'text':
    case 'longtext':
    case 'url':
      return 'string'
    case 'number':
    case 'rating':
    case 'score':
      return 'number'
    case 'select':
    case 'dropdownsingle':
      return 'singleSelect'
    case 'multiselect':
      return 'multiSelect'
    default:
      return null
  }
}

/**
 * Builds a map from structured name (trigger_form__<elementId>) → workflow
 * category + option labels, including elements nested inside sections.
 */
function buildFormElementMap(form: Form): Map<string, FormElementInfo> {
  const map = new Map<string, FormElementInfo>()

  const processElement = (element: Element) => {
    if (!element.mandatory) return

    const category = getWorkflowCategory(element.getType())
    if (!category) return

    const structuredName = buildTriggerFormKey(element.id)
    map.set(structuredName, {
      workflowCategory: category,
      optionLabels: new Set(element.options.map((o) => o.label)),
    })
  }

  for (const element of form.elements) {
    if (element instanceof SectionElement) {
      for (const child of element.elements) {
        processElement(child)
      }
    } else {
      processElement(element)
    }
  }

  return map
}

const isConditionNode = (node: NodeUI): node is NodeUICondition => node.type === 'condition'

type DynamicInputUsage = {
  variableName: string
  operator: string
  rightValue: string
  rightType: string
}

/**
 * Extracts all dynamic input comparisons from UI condition nodes (when workflow tab is mounted).
 */
function getDynamicInputUsagesFromUINodes(nodes: NodeUI[]): DynamicInputUsage[] {
  const usages: DynamicInputUsage[] = []

  for (const node of nodes) {
    if (!isConditionNode(node)) continue

    for (const comparison of node.data.comparisons) {
      for (const expr of comparison.expression.root.expressions) {
        if (expr.left.source === 'dynamic_input' && expr.left.name) {
          usages.push({
            variableName: expr.left.name,
            operator: expr.operator,
            rightValue: expr.right.value,
            rightType: expr.right.type,
          })
        }
      }
    }
  }

  return usages
}

/**
 * Extracts all dynamic input comparisons from server-side workflow nodes.
 * Used as fallback when the WorkflowContainer is not mounted (e.g. form tab is active).
 */
function getDynamicInputUsagesFromServerData(
  workflowNodes: WorkflowContainerFragment['workflowNodes']
): DynamicInputUsage[] {
  const usages: DynamicInputUsage[] = []

  const backendNodes = deserializeWorkflowNodes(workflowNodes)

  for (const node of backendNodes) {
    if (!isBackendSwitchNode(node)) continue

    for (const comparison of node.data.comparisons) {
      for (const expr of comparison.expression.root.expressions) {
        if (expr.left.source === 'dynamic_input' && expr.left.name) {
          usages.push({
            variableName: expr.left.name,
            operator: expr.operator,
            rightValue: expr.right.value,
            rightType: expr.right.type,
          })
        }
      }
    }
  }

  return usages
}

/**
 * Determines the expected workflow category from a condition's right-hand literal type
 * and operator. This is the inverse of `getLiteralType` in `literalTypes.ts`.
 */
function getExpectedCategoryFromCondition(usage: DynamicInputUsage): WorkflowCategory {
  if (usage.operator === 'contains' || usage.operator === 'not_contains') {
    return 'multiSelect'
  }

  if (usage.operator === 'one_of' || usage.operator === 'not_one_of') {
    return usage.rightType === 'string_array' ? 'singleSelect' : 'number'
  }

  switch (usage.rightType) {
    case 'integer':
      return 'number'
    case 'float':
      return 'number'
    case 'string':
      return 'string'
    default:
      return 'string'
  }
}

/**
 * Returns true when the old category (from the condition) can work
 * with the new category (from the current form element).
 */
function areCategoriesCompatible(
  conditionCategory: WorkflowCategory,
  formCategory: WorkflowCategory
): boolean {
  if (conditionCategory === formCategory) return true

  // singleSelect conditions using eq/neq store a string literal, so
  // conditionCategory is inferred as 'string'. This remains compatible
  // when the form element is still singleSelect (both produce scalars).
  // multiSelect is NOT compatible because its runtime value is an array,
  // which would break scalar operators like eq/neq.
  if (conditionCategory === 'string' && formCategory === 'singleSelect') return true

  return false
}

/**
 * Checks whether a condition's right-hand value is still valid given the
 * current form element options. Only relevant for singleSelect/multiSelect elements.
 */
function isConditionValueValidForOptions(
  usage: DynamicInputUsage,
  elementInfo: FormElementInfo
): string[] {
  if (
    elementInfo.workflowCategory !== 'singleSelect' &&
    elementInfo.workflowCategory !== 'multiSelect'
  ) {
    return []
  }

  if (!usage.rightValue || usage.rightValue === '' || usage.rightValue === '[]') {
    return []
  }

  const missingOptions: string[] = []

  if (usage.operator === 'one_of' || usage.operator === 'not_one_of') {
    const values = convertArrayFromWorkflowValue<string>(usage.rightValue)
    for (const v of values) {
      const stringVal = String(v)
      if (!elementInfo.optionLabels.has(stringVal)) {
        missingOptions.push(stringVal)
      }
    }
  } else {
    if (!elementInfo.optionLabels.has(usage.rightValue)) {
      missingOptions.push(usage.rightValue)
    }
  }

  return missingOptions
}

export type ConsistencyIssue = {
  variableName: string
  reason: 'removed' | 'type_changed' | 'invalid_options'
  details?: string
}

export type FormWorkflowConsistencyResult = {
  isValid: boolean
  issues: ConsistencyIssue[]
}

/**
 * Validates that the current form is still consistent with workflow conditions.
 *
 * Uses UI nodes when the workflow tab is mounted (may have unsaved changes),
 * falls back to server-side workflow data otherwise.
 *
 * Checks:
 * 1. Variable name still exists as a form element
 * 2. Element type hasn't changed in an incompatible way
 * 3. For select/multiselect elements, condition values reference valid options
 */
export function validateFormWorkflowConsistency(
  form: Form,
  nodes: NodeUI[],
  workflow: WorkflowContainerFragment
): FormWorkflowConsistencyResult {
  const elementMap = buildFormElementMap(form)

  const hasUINodes = nodes.some((n) => n.type === 'condition')
  const usages = hasUINodes
    ? getDynamicInputUsagesFromUINodes(nodes)
    : getDynamicInputUsagesFromServerData(workflow.workflowNodes)

  const issues: ConsistencyIssue[] = []
  const reported = new Set<string>()

  for (const usage of usages) {
    const key = `${usage.variableName}:${usage.operator}:${usage.rightValue}`

    const elementInfo = elementMap.get(usage.variableName)

    if (!elementInfo) {
      const nameKey = `removed:${usage.variableName}`
      if (!reported.has(nameKey)) {
        reported.add(nameKey)
        issues.push({ variableName: usage.variableName, reason: 'removed' })
      }
      continue
    }

    const conditionCategory = getExpectedCategoryFromCondition(usage)
    if (!areCategoriesCompatible(conditionCategory, elementInfo.workflowCategory)) {
      const typeKey = `type:${usage.variableName}`
      if (!reported.has(typeKey)) {
        reported.add(typeKey)
        issues.push({ variableName: usage.variableName, reason: 'type_changed' })
      }
      continue
    }

    const missingOptions = isConditionValueValidForOptions(usage, elementInfo)
    if (missingOptions.length > 0 && !reported.has(key)) {
      reported.add(key)
      issues.push({
        variableName: usage.variableName,
        reason: 'invalid_options',
        details: missingOptions.join(', '),
      })
    }
  }

  return {
    isValid: issues.length === 0,
    issues,
  }
}

function resolveVariableLabel(variableName: string, elementLabels: Map<string, string>): string {
  const referenceId = parseTriggerFormKey(variableName)
  if (!referenceId) return variableName

  return elementLabels.get(referenceId) ?? variableName
}

export function buildElementLabelMap(form: Form | undefined): Map<string, string> {
  const map = new Map<string, string>()
  if (!form) return map

  const processElement = (element: Element) => {
    if (element.id && element.label) {
      map.set(element.id, element.label)
    }
  }

  for (const element of form.elements) {
    if (element instanceof SectionElement) {
      for (const child of element.elements) {
        processElement(child)
      }
    } else {
      processElement(element)
    }
  }

  return map
}

export function getAffectedVariableNames(
  issues: ConsistencyIssue[],
  elementLabels?: Map<string, string>
): string {
  const uniqueNames = [...new Set(issues.map((i) => i.variableName))]

  if (!elementLabels) return uniqueNames.join(', ')

  return uniqueNames.map((name) => resolveVariableLabel(name, elementLabels)).join(', ')
}
